import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from "vue";
  import { useStore } from "vuex";
  import { useToast } from "vue-toastification";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import ExportPDFModal from "@/v2/components/ui/modals/ExportPDFModal.vue";
  import type { StageInterface } from "@/v2/types/StageInterface";
  import type { LotInterface } from "@/types/LotInterface";
  import {
    BarChart3,
    CheckCircle,
    Download,
    Home,
    Clock,
    FileText,
  } from "lucide-vue-next";
  import calculateStageCompletion from "../helpers/calculateStageCompletion";
  import calculateTotalLotsByStatus from "../helpers/calculateTotalLotsByStatus";
  import sumAllStagesRevenue from "../helpers/sumAllStagesRevenue";
  import calculateStageRevenuePerStatus from "../helpers/calculateStageRevenuePerStatus";
  import { formatPrice } from "@/helpers/transformation/price";
  import { formatDate } from "@/helpers/date";
  import { LotStatus } from "@/enums/LotStatus";
  import { getAllLotsForReports } from "../helpers/getLotsDataForReports";
  import getLotBuyers from "../helpers/getLotBuyers";
  import { AuditLogInterface } from "../types/AuditLogInterface";
  import calculateDaysOnHold from "../helpers/calculateDaysOnHold";
  import classifyActiveContractLots, {
    ClassifiedLotsResult,
  } from "../helpers/classifyActiveContractLots";
  import { prepareExportData } from "../helpers/prepareExportData";
  import filterAuditLogsByPeriod from "../helpers/filterAuditLogsByPeriod";
  import calculateRecentSalesActivity, {
    SalesActivityResult,
  } from "../helpers/calculateRecentSalesActivity";
  import formatRelativeDate from "../helpers/formatRelativeDate";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'ReportsView',
  setup(__props, { expose: __expose }) {
  __expose();

  const store = useStore();
  const toast = useToast();
  const loading = ref(false);
  const exportLoading = ref(false);
  const showExportModal = ref(false);
  const selectedPeriod = ref({ label: "Last 30 days", value: "30days" });
  const selectedStageId = ref({ label: "All Stages", value: "all" });
  const selectedStage = ref<StageInterface | null>(null);
  const customDateFrom = ref("");
  const customDateTo = ref("");

  const lots = ref<LotInterface[]>([]);
  const stages = ref<StageInterface[]>([]);
  const lotsAuditLogs = ref<AuditLogInterface[]>([]);
  const totalLotsByStatus = ref<Record<string, number>>();
  const totalRevenuePerStatus = ref<Record<string, number>>();
  const classifiedActiveContractLots = ref<ClassifiedLotsResult>();

  const estateId = computed(() => store.state.user.selectedEstateId);

  const periods = [
    { label: "Last 30 days", value: "30days" },
    { label: "Last 3 Months", value: "3months" },
    { label: "Last 6 Months", value: "6months" },
    { label: "Last Year", value: "1year" },
  ];

  const stageOptions = computed(() => [
    { label: "All Stages", value: "all" },
    ...stages.value.map((stage) => ({
      label: `Stage ${stage.title}`,
      value: stage._id,
    })),
  ]);

  const filteredAuditLogs = computed(() => {
    return filterAuditLogsByPeriod(lotsAuditLogs.value, {
      period: selectedPeriod.value.value,
      customDateFrom: customDateFrom.value,
      customDateTo: customDateTo.value,
    });
  });

  const recentSalesActivity = computed((): SalesActivityResult => {
    return calculateRecentSalesActivity(
      filteredAuditLogs.value,
      stages.value,
      selectedStageId.value.value
    );
  });

  const hasAnyActivity = computed(() => {
    const details = recentSalesActivity.value.details;

    return (
      details.settlements.length > 0 ||
      details.contractsSigned.length > 0 ||
      details.contractsTerminated.length > 0 ||
      details.putOnHold.length > 0 ||
      details.holdsProgressed.length > 0 ||
      details.holdsCancelled.length > 0 ||
      details.wentUnconditional.length > 0
    );
  });

  const allContractsActivity = computed(() => {
    const details = recentSalesActivity.value.details;
    const combined = [
      ...details.contractsSigned.map((item) => ({
        ...item,
        isTerminated: false,
      })),
      ...details.contractsTerminated.map((item) => ({
        ...item,
        isTerminated: true,
      })),
    ];

    return combined.sort((a, b) => b.date.getTime() - a.date.getTime());
  });

  const changeFilter = (period: { label: string; value: string }) => {
    selectedPeriod.value = period;
  };

  const changeStage = (stage: { label: string; value: string }) => {
    selectedStageId.value = stage;
    selectedStage.value =
      stages.value.find((s) => s._id === stage.value) || null;
  };

  const exportData = () => {
    showExportModal.value = true;
  };

  const handleGeneratePDF = async (sections: Record<string, unknown>) => {
    showExportModal.value = false;

    if (
      !estateId.value ||
      !stages.value ||
      !totalLotsByStatus.value ||
      !totalRevenuePerStatus.value ||
      !classifiedActiveContractLots.value
    ) {
      toast.error("Unable to export: Required data is not available");
      return;
    }

    const selectedEstate = store.state.user.landownerEstates.find(
      (estate: { _id: string }) => estate._id === estateId.value
    );
    const estateName = selectedEstate?.title || "Estate Report";
    const estateLogoUrl = selectedEstate?.logoUrl || "";

    try {
      exportLoading.value = true;

      const exportPayload = prepareExportData({
        estateId: estateId.value,
        estateName,
        estateLogoUrl,
        sections: sections as {
          overview: { selected: boolean };
          breakdown: { selected: boolean; stages: Record<string, boolean> };
          activity: { selected: boolean; stages: Record<string, boolean> };
        },
        stages: stages.value,
        totalLotsByStatus: totalLotsByStatus.value,
        totalRevenuePerStatus: totalRevenuePerStatus.value,
        classifiedActiveContractLots: classifiedActiveContractLots.value,
        lotsAuditLogs: lotsAuditLogs.value,
        selectedStageId: selectedStageId.value,
        sumAllStagesRevenue: sumAllStagesRevenue(stages.value),
      });

      const response = await store.dispatch(
        "reports/exportReport",
        exportPayload
      );
      const pdfUrl = response.data.pdfLink;

      if (pdfUrl) {
        toast.success("PDF report opened in a new window!");
        window.open(pdfUrl, "_blank");
      } else {
        throw new Error("No PDF link received from server");
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF report. Please try again.");
    } finally {
      exportLoading.value = false;
    }
  };

  const fetchData = async () => {
    if (!estateId.value) return;

    loading.value = true;

    try {
      const fetchedStages = await store.dispatch("reports/getStagesReports", {
        estateId: estateId.value,
      });

      let fetchedLotAuditLogs;

      try {
        fetchedLotAuditLogs = await store.dispatch("reports/getLotsAuditLogs", {
          estateId: estateId.value,
        });
        lotsAuditLogs.value = fetchedLotAuditLogs?.data || [];
      } catch (auditError) {
        console.warn(auditError);
        lotsAuditLogs.value = [];
      }

      stages.value = fetchedStages.data;

      totalLotsByStatus.value = calculateTotalLotsByStatus(fetchedStages.data);
      totalRevenuePerStatus.value = calculateStageRevenuePerStatus(
        fetchedStages.data
      );
      classifiedActiveContractLots.value = classifyActiveContractLots(
        fetchedStages.data
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      loading.value = false;
    }
  };

  watch(
    () => estateId.value,
    () => {
      if (estateId.value) {
        fetchData();
      }
    }
  );

  onMounted(() => {
    if (estateId.value) {
      fetchData();
    }
  });

  const getConditionBadgeClass = (condition: string | null) => {
    if (!condition) return "condition-no-conditions";

    const conditionLower = condition.toLowerCase();

    if (conditionLower.includes("sale of home")) {
      return "condition-sale-of-home";
    } else if (conditionLower.includes("finance")) {
      return "condition-finance";
    } else if (conditionLower.includes("soil test")) {
      return "condition-soil-test";
    } else if (conditionLower.includes("council approval")) {
      return "condition-council-approval";
    } else {
      return "condition-no-conditions";
    }
  };

const __returned__ = { store, toast, loading, exportLoading, showExportModal, selectedPeriod, selectedStageId, selectedStage, customDateFrom, customDateTo, lots, stages, lotsAuditLogs, totalLotsByStatus, totalRevenuePerStatus, classifiedActiveContractLots, estateId, periods, stageOptions, filteredAuditLogs, recentSalesActivity, hasAnyActivity, allContractsActivity, changeFilter, changeStage, exportData, handleGeneratePDF, fetchData, getConditionBadgeClass, computed, onMounted, ref, watch, get useStore() { return useStore }, get useToast() { return useToast }, DropDown, ExportPDFModal, get BarChart3() { return BarChart3 }, get CheckCircle() { return CheckCircle }, get Download() { return Download }, get Home() { return Home }, get Clock() { return Clock }, get FileText() { return FileText }, get calculateStageCompletion() { return calculateStageCompletion }, get calculateTotalLotsByStatus() { return calculateTotalLotsByStatus }, get sumAllStagesRevenue() { return sumAllStagesRevenue }, get calculateStageRevenuePerStatus() { return calculateStageRevenuePerStatus }, get formatPrice() { return formatPrice }, get formatDate() { return formatDate }, get LotStatus() { return LotStatus }, get getAllLotsForReports() { return getAllLotsForReports }, get getLotBuyers() { return getLotBuyers }, get AuditLogInterface() { return AuditLogInterface }, get calculateDaysOnHold() { return calculateDaysOnHold }, get classifyActiveContractLots() { return classifyActiveContractLots }, get ClassifiedLotsResult() { return ClassifiedLotsResult }, get prepareExportData() { return prepareExportData }, get filterAuditLogsByPeriod() { return filterAuditLogsByPeriod }, get calculateRecentSalesActivity() { return calculateRecentSalesActivity }, get SalesActivityResult() { return SalesActivityResult }, get formatRelativeDate() { return formatRelativeDate } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})